import { Component } from '@angular/core';

@Component({
    selector: 'customer-transition-communication',
    template: 
    `
    <pet-page-template [data]='pageData' [additionalContent]='additionalContent' [status]='"Draft"'></pet-page-template>
    `,
    styles: ``
  })
export class CustomerTransitionComponent {
    public pageData: any = [
        {
          type: 'intro',
          value: {
            mainText: 'Introduction',
            subText:
              'Customer Transition and Communication focuses on preparing and guiding customers through the end of life of a product in a predictable and transparent way. The aim is to minimise disruption, maintain trust, and make sure customers have a clear path forward. This phase covers mapping impacted customers, choosing a transition strategy, planning and executing deprecation communication, aligning support wind down, and handling commercial implications in a structured manner.'
          }
        },
    
        {
          type: 'tileItem',
          value: {
            title: 'Roles and Responsibilities',
            items: [
              {
                name: 'Product Manager',
                description:
                  'Owns the overall transition plan for customers, ensures it reflects the sunset decision, and coordinates communication, timelines, and migration paths across functions.'
              },
              {
                name: 'Customer Success',
                description:
                  'Leads customer facing engagement, explains the decision and transition path, manages expectations, and acts as the main contact point for key accounts during the phase.'
              },
              {
                name: 'Account Management',
                description:
                  'Aligns commercial terms where required, supports conversations with strategic or sensitive customers, and ensures that transitions are reflected in pipeline and forecasts.'
              },
              {
                name: 'Architect',
                description:
                  'Provides input on feasible migration options, technical dependencies, and any constraints that may affect timelines or the scope of the transition.'
              },
              {
                name: 'Support',
                description:
                  'Handles incoming questions, incidents, and requests related to the deprecation and transition, and ensures that support policies are understood and applied consistently.'
              },
              {
                name: 'Product Marketing',
                description:
                  'Supports creation and review of external messaging, including announcements, FAQs, documentation updates, and web content where needed.'
              },
              {
                name: 'Legal',
                description:
                  'Advises on contractual obligations, notice periods, and any regulatory or commercial requirements that influence the timing and content of customer communication.'
              }
            ]
          }
        },
    
        {
          type: 'checkListItem',
          value: {
            title: 'Prerequisites',
            items: [
              {
                id: 1,
                text:
                  'A formally recorded Sunset Decision that describes the scope of end of life and the high level replacement or alternate path.'
              },
              {
                id: 2,
                text:
                  'An initial view of the customer base that is impacted, including key accounts, regions, segments, and partner relationships.'
              },
              {
                id: 3,
                text:
                  'Clarity on contractual obligations such as notice periods, service level commitments, and commercial terms that may restrict or guide deprecation timelines.'
              },
              {
                id: 4,
                text:
                  'Agreement between Product, Customer Success, Sales, and Tech Lead on the main options for migration or transition.'
              }
            ]
          }
        },
    
        {
          type: 'superListItem',
          value: {
            title: 'Process Steps',
            items: [
              {
                title: 'Map Impacted Customers and Usage',
                description: {
                  'Objective': [
                    'Identify who is impacted by the sunset and how critical the product is for their operations.'
                  ],
                  'Activity': [
                    'Segment customers based on usage, contract type, geography, and strategic importance.',
                    'Review telemetry, usage reports, and support history to understand how and where the product is used.',
                    'Highlight key accounts, regulated customers, and any high risk or high sensitivity cases.'
                  ],
                  'Outcome': [
                    'A structured view of impacted customers with an understanding of risk and priority.'
                  ]
                }
              },
              {
                title: 'Define Transition Strategy and Options',
                description: {
                  'Objective': [
                    'Decide how different customer groups will transition from the product and in what sequence.'
                  ],
                  'Activity': [
                    'Group customers into logical cohorts such as low usage, standard usage, and strategic accounts.',
                    'Decide whether to use a phased approach, a big bang approach, or a mixed model depending on complexity and risk.',
                    'Align on default migration options for each cohort and identify where individual handling is needed.'
                  ],
                  'Outcome': [
                    'A transition strategy that links cohorts, timelines, and migration options to clear actions.'
                  ]
                }
              },
              {
                title: 'Plan Deprecation Communication',
                description: {
                  'Objective': [
                    'Prepare clear, timely, and consistent communication that explains the change, timelines, and next steps.'
                  ],
                  'Activity': [
                    'Define key messages that explain the reason for end of life, what changes, and what does not change for customers.',
                    'Prepare communication assets such as email templates, FAQs, help centre articles, and internal talking points.',
                    'Decide which channels will be used for which customers, for example direct contact for key accounts and standard announcements for others.'
                  ],
                  'Outcome': [
                    'A concrete communication plan with messages, audiences, channels, and a communication schedule.'
                  ]
                }
              },
              {
                title: 'Execute Transition and Migration Support',
                description: {
                  'Objective': [
                    'Help customers move to replacement solutions or alternate paths in a controlled and supported way.'
                  ],
                  'Activity': [
                    'Send deprecation announcements according to the plan and follow up with strategic accounts through Customer Success or Sales.',
                    'Provide migration guides, workshops, or assisted onboarding for replacement products where applicable.',
                    'Track progress of customer transitions and identify accounts that need extra support or risk management.'
                  ],
                  'Outcome': [
                    'Customers have practical support to move away from the product and adopt replacement solutions where available.'
                  ]
                }
              },
              {
                title: 'Align Support Wind Down and Exception Handling',
                description: {
                  'Objective': [
                    'Manage the reduction and eventual end of support in a way that is predictable and fair.'
                  ],
                  'Activity': [
                    'Define what support will be provided during the transition window, for example critical fixes only and no new feature work.',
                    'Communicate the support policy clearly, including last dates for standard support and any extended support offered to specific customers.',
                    'Set up a simple process for handling justified exceptions or escalations that may require temporary deviations from the standard plan.'
                  ],
                  'Outcome': [
                    'A controlled support wind down that is understood by both internal teams and customers, with known end dates and clear exception handling.'
                  ]
                }
              }
            ]
          }
        },
    
        {
          type: 'multibulletList',
          value: {
            title: 'Key Inputs for Customer Transition and Communication',
            items: [
              {
                name: 'Customer and Contract Overview',
                description: [
                  'Description: List of customers, contracts, renewal dates, notice periods, and any specific obligations related to service continuity.',
                  'Example: A view that highlights customers with renewals inside the planned deprecation window or contracts that require extended notice.'
                ]
              },
              {
                name: 'Usage and Dependency Data',
                description: [
                  'Description: Telemetry and usage reports that show how actively the product is used and which features are most critical.',
                  'Example: Identification of customers that rely on a specific module that has no direct replacement and may need special handling.'
                ]
              },
              {
                name: 'Risk and Impact Assessment',
                description: [
                  'Description: Assessment of operational, commercial, and reputational risk associated with the transition for different customer groups.',
                  'Example: Marking certain accounts as high impact due to regulatory requirements or business critical use cases.'
                ]
              },
              {
                name: 'Migration Options and Readiness',
                description: [
                  'Description: Overview of available replacement products, partner solutions, or workarounds, including their readiness and known gaps.',
                  'Example: A matrix that shows which customer segments can be moved to which replacement product, with notes on limitations.'
                ]
              },
              {
                name: 'Approved Messaging and Materials',
                description: [
                  'Description: Finalised communication templates, FAQs, internal briefing notes, and support scripts.',
                  'Example: A standard deprecation email template and FAQ that can be adapted slightly per segment or region.'
                ]
              }
            ]
          }
        },
    
        {
          type: 'listItem',
          value: {
            title: 'Outcomes of Effective Customer Transition and Communication',
            items: [
              {
                name: 'Predictable customer experience',
                description:
                  'Customers receive clear information in good time, understand why the change is happening, and know what to expect and when.'
              },
              {
                name: 'Structured migration path',
                description:
                  'Each customer segment has a defined path to a replacement solution or alternate way of working, with support that matches complexity and risk.'
              },
              {
                name: 'Managed commercial and contractual impact',
                description:
                  'Contractual obligations, renewals, and commercial discussions are handled in a coordinated way, avoiding surprises for both customers and internal teams.'
              },
              {
                name: 'Protected trust and brand',
                description:
                  'The organisation demonstrates that it handles end of life decisions in a professional and customer centric way, reducing the risk of churn driven by frustration or confusion.'
              }
            ]
          }
        }
      ];
    
      public additionalContent = [
        {
          type: 'tips',
          title: 'Tips',
          value:
            'Treat customer transition as a relationship activity rather than a pure operational task. Clear messaging, realistic timelines, and proactive support often matter more to customers than the fact that a product is reaching end of life.'
        },
        {
          type: 'attachments',
          title: 'Templates / Guides',
          value: [
            {
              name: 'EOL Customer Communication Plan (underway)',
              url: '#'
            },
            {
              name: 'Customer Impact and Cohort Mapping Sheet (underway)',
              url: '#'
            }
          ]
        },
        {
          type: 'optionalContent',
          title: 'Benefits and Repercussions',
          value: [
            {
              key: 'Benefits',
              data: [
                'Reduces surprise and frustration by giving customers time and support to adapt.',
                'Improves chances that customers move to recommended replacement products instead of leaving for competitors.',
                'Strengthens the perception that the organisation acts transparently and responsibly throughout the product lifecycle.'
              ]
            },
            {
              key: 'Repercussions',
              data: [
                'Weak or late communication can lead to customer churn, escalations, and damage to reputation.',
                'Lack of a clear migration path may force customers to find their own alternatives with little interest in staying in the ecosystem.',
                'Uncoordinated handling of contracts and renewals can create revenue leakage or legal disputes.'
              ]
            }
          ]
        }
      ];
    }
